import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { tr } from "date-fns/locale/tr";
import { Clock, Eye, User, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import axios from "axios";
import { API } from "../lib/api";
import { useMember } from "../context/MemberContext";

const HeroSlider = ({ news }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [ads, setAds] = useState([]);
  const { member } = useMember();
  const isPremium = member?.role === "premium";

  const featuredNews = news?.slice(0, 5) || [];

  useEffect(() => {
    if (!isPremium) {
      axios.get(`${API}/ads?placement=slider`).then(r => setAds(r.data || [])).catch(() => {});
    }
  }, [isPremium]);

  // Interleave: news, ad, news, ad...
  const slides = [];
  let adIdx = 0;
  featuredNews.forEach((item, i) => {
    slides.push({ type: "news", data: item });
    if (!isPremium && ads.length > 0 && i < featuredNews.length - 1) {
      slides.push({ type: "ad", data: ads[adIdx % ads.length] });
      adIdx++;
    }
  });

  const totalSlides = slides.length;

  const goToSlide = useCallback((index) => {
    setCurrentIndex(index);
  }, []);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? totalSlides - 1 : prev - 1));
  }, [totalSlides]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === totalSlides - 1 ? 0 : prev + 1));
  }, [totalSlides]);

  // Autoplay
  useEffect(() => {
    if (!isAutoPlaying || totalSlides <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev === totalSlides - 1 ? 0 : prev + 1));
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, totalSlides]);

  if (!news || news.length === 0) return null;

  const currentItem = slides[currentIndex];

  return (
    <div 
      className="relative h-full overflow-hidden rounded-2xl" 
      data-testid="hero-slider"
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
    >
      {/* Slides */}
      <div 
        className="flex h-full transition-transform duration-500 ease-out"
        style={{ transform: `translateX(-${currentIndex * 100}%)` }}
      >
        {slides.map((slide, index) => (
          <div key={index} className="flex-shrink-0 w-full h-full">
            {slide.type === "news" ? (
              <Link 
                to={`/haber/${slide.data.slug || slide.data.id}`} 
                className="block group relative h-full"
                data-testid={`hero-slide-${index}`}
              >
                <div className="relative aspect-[16/9] md:aspect-[21/9] overflow-hidden">
                  <img 
                    src={slide.data.image_url} 
                    alt={slide.data.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=1200&h=600&fit=crop'; }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                    <span className="inline-block px-2 py-0.5 bg-amber-500 text-black text-xs font-bold rounded-full mb-2">
                      {slide.data.category}
                    </span>
                    <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-white mb-2 group-hover:text-amber-400 transition-colors line-clamp-2">
                      {slide.data.title}
                    </h2>
                    <p className="text-zinc-300 text-sm max-w-2xl line-clamp-1 hidden md:block">{slide.data.summary}</p>
                    <div className="flex items-center gap-3 mt-2 text-zinc-400 text-xs">
                      <span className="flex items-center gap-1"><User className="w-3 h-3" />{slide.data.author}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{format(new Date(slide.data.created_at), "d MMM", { locale: tr })}</span>
                      <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{slide.data.views}</span>
                    </div>
                  </div>
                </div>
              </Link>
            ) : (
              /* Ad Slide */
              <a
                href={slide.data?.link || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="block group relative h-full"
                data-testid={`hero-ad-${index}`}
                onClick={() => { axios.post(`${API}/ads/${slide.data?.id}/click`).catch(() => {}); }}
              >
                <div className="relative aspect-[16/9] md:aspect-[21/9] overflow-hidden">
                  <img 
                    src={slide.data?.image_url || 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=1200&h=600&fit=crop'}
                    alt={slide.data?.title || 'Reklam'}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                  <div className="absolute top-3 right-3 px-2 py-0.5 bg-black/60 backdrop-blur-sm text-zinc-400 text-[10px] rounded-full border border-white/10">
                    Reklam
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                    <span className="inline-block px-2 py-0.5 bg-zinc-700 text-zinc-300 text-xs font-medium rounded-full mb-2">
                      Sponsorlu
                    </span>
                    <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-white mb-2 group-hover:text-amber-400 transition-colors line-clamp-2">
                      {slide.data?.title || 'Reklam Alanı'}
                    </h2>
                    <p className="text-zinc-300 text-sm max-w-2xl line-clamp-1 hidden md:block">{slide.data?.description || ''}</p>
                    <div className="flex items-center gap-2 mt-2 text-zinc-400 text-xs">
                      <ExternalLink className="w-3 h-3" />
                      <span>{slide.data?.advertiser || 'Sponsor'}</span>
                    </div>
                  </div>
                </div>
              </a>
            )}
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={goToPrevious}
        className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 bg-black/50 hover:bg-black/70 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all z-10"
        data-testid="hero-slider-prev"
      >
        <ChevronLeft className="w-4 h-4 md:w-5 md:h-5" />
      </button>
      <button
        onClick={goToNext}
        className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 bg-black/50 hover:bg-black/70 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all z-10"
        data-testid="hero-slider-next"
      >
        <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
      </button>

      {/* Dots Indicator */}
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10">
        {slides.map((slide, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`h-1.5 rounded-full transition-all ${
              index === currentIndex 
                ? 'w-4 bg-amber-500' 
                : 'w-1.5 bg-white/40 hover:bg-white/60'
            }`}
            data-testid={`hero-dot-${index}`}
          />
        ))}
      </div>
    </div>
  );
};

export default HeroSlider;
